import { MonthSegment } from '../product-costs.model';
import type { ProductCostPrice, TimelineSegment } from '../product-costs.model';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Parses an ISO/Date string into a Date object.
 * @param s - string representing a date
 * @returns Date object
 */
function parseDate(s: string): Date {
	return new Date(s);
}

/**
 * Returns the date set to the start of the day (00:00:00) in local timezone.
 * @param d - date to normalize
 * @returns new date with zero time
 */
export function startOfDay(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Normalizes a date for use as the end of a date range (start of that day at midnight).
 * @param d - date (e.g. from a date picker)
 * @returns date at midnight (same day)
 */
export function normalizeDateRangeEnd(d: Date): Date {
	return startOfDay(d);
}

/**
 * Returns the first day of the current month at 00:00:00 in local timezone.
 * @returns new date (1st day of current month, midnight)
 */
export function firstDayOfCurrentMonth(): Date {
	const d = new Date();
	d.setDate(1);
	d.setHours(0, 0, 0, 0);
	return d;
}

/**
 * Returns the last day of the current month at 00:00:00 in local timezone.
 * @returns new date (last day of current month, midnight)
 */
export function lastDayOfCurrentMonth(): Date {
	const d = new Date();
	d.setMonth(d.getMonth() + 1);
	d.setDate(0);
	d.setHours(0, 0, 0, 0);
	return d;
}

/**
 * Converts a date to UTC noon timestamp for that day (used for day counting without timezone impact).
 * @param d - date
 * @returns milliseconds since epoch (UTC noon)
 */
function calendarDayToNoonUTC(d: Date): number {
	const norm = startOfDay(d);
	return Date.UTC(norm.getFullYear(), norm.getMonth(), norm.getDate(), 12, 0, 0);
}

/**
 * Counts the number of days (inclusive) between rangeStart and rangeEnd.
 * @param rangeStart - start of range
 * @param rangeEnd - end of range
 * @returns number of days (>= 0)
 */
export function countDaysInRange(rangeStart: Date, rangeEnd: Date): number {
	const start = calendarDayToNoonUTC(rangeStart);
	const end = calendarDayToNoonUTC(rangeEnd);
	return Math.max(0, Math.round((end - start) / MS_PER_DAY) + 1);
}

/**
 * Returns the day index (1..daysInRangeVal) for the given date in the range; out-of-range values are clamped.
 * @param date - date
 * @param rangeStart - start of range (normalized to start of day)
 * @param daysInRangeVal - total number of days in range
 * @returns day index in [1, daysInRangeVal]
 */
function getDayIndexInRange(date: Date, rangeStart: Date, daysInRangeVal: number): number {
	const start = calendarDayToNoonUTC(rangeStart);
	const d = calendarDayToNoonUTC(date);
	const index = 1 + Math.round((d - start) / MS_PER_DAY);
	return Math.max(1, Math.min(index, daysInRangeVal));
}

/**
 * Position on the [0, 1] axis corresponding to the middle of the given day in the range.
 * @param day - day index (1..daysInRangeVal)
 * @param daysInRangeVal - total number of days in range
 * @returns value in (0, 1)
 */
function positionMiddleOfDay(day: number, daysInRangeVal: number): number {
	return (day - 0.5) / daysInRangeVal;
}

/**
 * Position on the [0, 1] axis corresponding to the end of the given day in the range.
 * @param day - day index (1..daysInRangeVal)
 * @param daysInRangeVal - total number of days in range
 * @returns value in [0, 1]
 */
function positionEndOfDay(day: number, daysInRangeVal: number): number {
	return day / daysInRangeVal;
}

/**
 * Returns a new date equal to date + days (preserving local timezone).
 * @param date - base date
 * @param days - number of days to add (may be negative)
 * @returns new date
 */
function addDays(date: Date, days: number): Date {
	const result = new Date(date.getTime());
	result.setDate(result.getDate() + days);
	return result;
}

const POLISH_MONTH_NAMES = [
	'styczeń',
	'luty',
	'marzec',
	'kwiecień',
	'maj',
	'czerwiec',
	'lipiec',
	'sierpień',
	'wrzesień',
	'październik',
	'listopad',
	'grudzień',
];

/**
 * Returns the Polish month name for the given date.
 * @param date - date
 * @returns month name (e.g. "styczeń")
 */
function getMonthLabel(date: Date): string {
	return POLISH_MONTH_NAMES[date.getMonth()];
}

/**
 * Builds month segments for the date range (labels and width percentages).
 * @param rangeStart - start of range
 * @param rangeEnd - end of range
 * @returns array of segments with label and widthPercent
 */
export function buildMonthSegments(rangeStart: Date, rangeEnd: Date): MonthSegment[] {
	const start = startOfDay(rangeStart);
	const end = startOfDay(rangeEnd);
	const totalDays = countDaysInRange(rangeStart, rangeEnd);
	if (totalDays < 1) {
		return [];
	}
	const segments: MonthSegment[] = [];
	let current = new Date(start.getTime());
	while (current.getTime() <= end.getTime()) {
		const monthStart = new Date(current.getFullYear(), current.getMonth(), current.getDate());
		const lastDayOfMonth = new Date(current.getFullYear(), current.getMonth() + 1, 0);
		const segmentEndDate = lastDayOfMonth.getTime() > end.getTime() ? end : lastDayOfMonth;
		const segmentStartNorm = startOfDay(monthStart);
		const segmentEndNorm = startOfDay(segmentEndDate);
		const daysInSegment = countDaysInRange(segmentStartNorm, segmentEndNorm);
		const widthPercent = (daysInSegment / totalDays) * 100;
		segments.push({ label: getMonthLabel(current), widthPercent });
		current = addDays(segmentEndDate, 1);
	}
	return segments;
}

/** Maximum number of days in range for which the day scale (day-of-month numbers) is shown. */
export const DAY_SCALE_THRESHOLD_DAYS = 92;

/**
 * Builds the day scale (day-of-month number) for each day in the range.
 * @param rangeStart - start of range
 * @param rangeEnd - end of range
 * @returns array of objects with dayOfMonth (1-31)
 */
export function buildDayScaleWithMonth(rangeStart: Date, rangeEnd: Date): { dayOfMonth: number }[] {
	const totalDays = countDaysInRange(rangeStart, rangeEnd);
	if (totalDays < 1) {
		return [];
	}
	const start = startOfDay(rangeStart);
	return Array.from({ length: totalDays }, (_, i) => {
		const d = addDays(start, i);
		return { dayOfMonth: d.getDate() };
	});
}

const TIMELINE_BOOTSTRAP_COLORS: { barClass: string; connectorColor: string }[] = [
	{ barClass: 'bg-primary', connectorColor: 'var(--bs-primary)' },
	{ barClass: 'bg-success', connectorColor: 'var(--bs-success)' },
	{ barClass: 'bg-danger', connectorColor: 'var(--bs-danger)' },
	{ barClass: 'bg-warning', connectorColor: 'var(--bs-warning)' },
	{ barClass: 'bg-info', connectorColor: 'var(--bs-info)' },
];

/** Price range clipped to the displayed range, with associated price. */
export interface ClippedPriceRange {
	start: Date;
	end: Date;
	price: ProductCostPrice;
}

/**
 * Clips price ranges to the given date interval and sorts by start date.
 * Skips prices with invalid or empty range.
 * @param prices - list of prices with dateRange (lower/upper)
 * @param rangeStart - start of displayed range
 * @param rangeEnd - end of displayed range
 * @returns sorted array of clipped ranges with price
 */
export function clipPricesToRange(prices: ProductCostPrice[], rangeStart: Date, rangeEnd: Date): ClippedPriceRange[] {
	const rangeStartNorm = startOfDay(rangeStart);
	const rangeEndNorm = startOfDay(rangeEnd);
	const clipped: ClippedPriceRange[] = [];
	for (const p of prices) {
		const start = p.dateRange.lower ? parseDate(p.dateRange.lower) : null;
		const end = p.dateRange.upper ? parseDate(p.dateRange.upper) : null;
		if (!start || !end || start > end) {
			continue;
		}
		const clipStart = start < rangeStartNorm ? rangeStartNorm : startOfDay(start);
		const clipEnd = end > rangeEndNorm ? rangeEndNorm : startOfDay(end);
		if (clipStart.getTime() > clipEnd.getTime()) {
			continue;
		}
		clipped.push({ start: clipStart, end: clipEnd, price: p });
	}
	clipped.sort((a, b) => a.start.getTime() - b.start.getTime());
	return clipped;
}

/**
 * Builds timeline segments (bars + gaps) from clipped price ranges.
 * Uses cumulativeWidth for positioning; adds gap segments between ranges and at the end.
 * @param clipped - sorted clipped price ranges
 * @param rangeStartNorm - start of range (start of day)
 * @param daysInRangeVal - total number of days in range
 * @returns array of TimelineSegment (with price for bars, without for gaps)
 */
function buildSegmentsFromClippedRanges(
	clipped: ClippedPriceRange[],
	rangeStartNorm: Date,
	daysInRangeVal: number,
): TimelineSegment[] {
	const segments: TimelineSegment[] = [];
	let cumulativeWidth = 0;
	let rangeIndex = 0;
	let lastEndDay: number | null = null;

	for (const clippedRange of clipped) {
		const startDay = getDayIndexInRange(clippedRange.start, rangeStartNorm, daysInRangeVal);
		const endDay = getDayIndexInRange(clippedRange.end, rangeStartNorm, daysInRangeVal);
		const prevEndPos = lastEndDay !== null ? positionMiddleOfDay(lastEndDay, daysInRangeVal) : 0;
		const segmentStartPos = positionMiddleOfDay(startDay, daysInRangeVal);
		const isLastDayOfRange = endDay === daysInRangeVal;
		const segmentEndPos = isLastDayOfRange
			? positionEndOfDay(endDay, daysInRangeVal)
			: positionMiddleOfDay(endDay, daysInRangeVal);

		if (segmentStartPos > prevEndPos) {
			const gapWidthPercent = (segmentStartPos - prevEndPos) * 100;
			const leftPercent = cumulativeWidth + gapWidthPercent / 2;
			segments.push({ widthPercent: gapWidthPercent, leftPercent });
			cumulativeWidth += gapWidthPercent;
		}
		const widthPercent = (segmentEndPos - segmentStartPos) * 100;
		const leftPercent = cumulativeWidth + widthPercent / 2;
		const bootstrapColor = TIMELINE_BOOTSTRAP_COLORS[rangeIndex % TIMELINE_BOOTSTRAP_COLORS.length];
		segments.push({
			widthPercent,
			leftPercent,
			price: clippedRange.price,
			barClass: bootstrapColor.barClass,
			color: bootstrapColor.connectorColor,
			connectorAbove: rangeIndex % 2 === 0,
		});
		cumulativeWidth += widthPercent;
		rangeIndex += 1;
		lastEndDay = endDay;
	}

	const lastSegmentEndsAtRangeEnd = lastEndDay === daysInRangeVal;
	const endPos =
		lastEndDay !== null
			? lastSegmentEndsAtRangeEnd
				? positionEndOfDay(lastEndDay, daysInRangeVal)
				: positionMiddleOfDay(lastEndDay, daysInRangeVal)
			: 0;
	const rangeEndPos = positionEndOfDay(daysInRangeVal, daysInRangeVal);
	if (endPos < rangeEndPos) {
		const gapWidthPercent = (rangeEndPos - endPos) * 100;
		const leftPercent = cumulativeWidth + gapWidthPercent / 2;
		segments.push({ widthPercent: gapWidthPercent, leftPercent });
	}

	return segments;
}

/**
 * Main entry: builds timeline segments for display from price list and date range.
 * Composes clipPricesToRange and buildSegmentsFromClippedRanges.
 * @param prices - product prices (with dateRange)
 * @param rangeStart - start of displayed range
 * @param rangeEnd - end of displayed range
 * @returns array of segments (price bars + gaps)
 */
export function buildTimelineSegments(prices: ProductCostPrice[], rangeStart: Date, rangeEnd: Date): TimelineSegment[] {
	const rangeStartNorm = startOfDay(rangeStart);
	const daysInRangeVal = countDaysInRange(rangeStart, rangeEnd);
	if (daysInRangeVal < 1) {
		return [];
	}
	const clipped = clipPricesToRange(prices, rangeStart, rangeEnd);
	return buildSegmentsFromClippedRanges(clipped, rangeStartNorm, daysInRangeVal);
}
